import { type AuthUser } from "wasp/auth";
import { useState } from "react";
import {
  useQuery,
  getInboxMessages,
  updateInboxMessageState,
} from "wasp/client/operations";
import { Button, SurfaceCard } from "../components/ui";
import Breadcrumb from "../admin/layout/Breadcrumb";
import DefaultLayout from "../admin/layout/DefaultLayout";
import { useRedirectHomeUnlessUserIsAdmin } from "../admin/useRedirectHomeUnlessUserIsAdmin";
import MessageThreadDrawer from "./MessageThreadDrawer";

const MessagesPage = ({ user }: { user: AuthUser }) => {
  useRedirectHomeUnlessUserIsAdmin({ user });
  const [filter, setFilter] = useState<"unread" | "replied">("unread");
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(
    null,
  );
  const { data, refetch, isLoading, error } = useQuery(getInboxMessages, {
    status: filter,
  });

  return (
    <DefaultLayout user={user}>
      <Breadcrumb pageName="Inbox" />
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap gap-2">
          {["unread", "replied"].map((option) => (
            <Button
              key={option}
              type="button"
              size="xs"
              variant={filter === option ? "solid" : "outline"}
              tone={filter === option ? "primary" : "neutral"}
              className="rounded-full"
              onClick={() => setFilter(option as "unread" | "replied")}
            >
              {option === "unread" ? "Unread" : "Replied"}
            </Button>
          ))}
        </div>
        {error && (
          <SurfaceCard>
            <p className="text-danger-600">
              Failed to load messages: {error.message}
            </p>
          </SurfaceCard>
        )}
        {isLoading && (
          <SurfaceCard>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Loading inbox messages...
            </p>
          </SurfaceCard>
        )}
        {(data ?? []).map((message) => (
          <SurfaceCard key={message.id}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-black dark:text-white">
                  {message.user.email ??
                    message.user.username ??
                    "Anonymous sender"}
                </p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  {new Date(message.createdAt).toLocaleString()}
                </p>
                <p className="mt-3 text-sm text-neutral-700 dark:text-neutral-300 line-clamp-2">
                  {message.content}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="xs"
                  variant="outline"
                  tone="neutral"
                  className="rounded-sm"
                  onClick={() => setSelectedMessageId(message.id)}
                >
                  Open thread
                </Button>
                {!message.isRead && (
                  <Button
                    type="button"
                    size="xs"
                    className="rounded-sm"
                    onClick={async () => {
                      await updateInboxMessageState({
                        id: message.id,
                        isRead: true,
                      });
                      await refetch();
                    }}
                  >
                    Mark read
                  </Button>
                )}
              </div>
            </div>
          </SurfaceCard>
        ))}
      </div>
      <MessageThreadDrawer
        messageId={selectedMessageId}
        messages={data ?? []}
        onOpenChange={(open) => {
          if (!open) setSelectedMessageId(null);
        }}
        onMarkReplied={async (id) => {
          await updateInboxMessageState({ id, markReplied: true });
          await refetch();
        }}
      />
    </DefaultLayout>
  );
};

export default MessagesPage;
